import React, { useEffect, useState } from "react";
import { Loader2, Calendar, Mail, Slack as SlackIcon, ChevronDown, ChevronUp, Zap, Copy, Check, RefreshCw, Terminal } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";

/**
 * Morning Bed Pack settings + history surface.
 *
 * Lives in the Imaging Studio bed tab. Lets producers:
 *   1. See when the next scheduled run fires (06:00 UTC, weekdays)
 *   2. Toggle the schedule on/off
 *   3. Configure Slack + Email digest channels
 *   4. Fire a manual "run now" outside the schedule
 *   5. See the last 7 runs (success/fail + import counts)
 */
export default function MorningPackPanel({ onRunComplete }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [runningNow, setRunningNow] = useState(false);
  const [status, setStatus] = useState(null);
  const [webhookDraft, setWebhookDraft] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/imaging/library/morning-pack/status");
      setStatus(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load Morning Bed Pack status");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const save = async (patch) => {
    setSaving(true);
    try {
      const r = await api.post("/imaging/library/morning-pack/settings", patch);
      setStatus(r.data);
      toast.success("Saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const saveWebhook = async () => {
    const w = webhookDraft.trim();
    if (w && !w.startsWith("https://hooks.slack.com/")) {
      toast.error("Slack webhooks must start with https://hooks.slack.com/");
      return;
    }
    await save({ slack_webhook: w });
    setWebhookDraft("");
  };

  const runNow = async () => {
    setRunningNow(true);
    toast.message("Running Morning Bed Pack now — this can take ~60s");
    try {
      const r = await api.post("/imaging/library/morning-pack/run-now", null, { timeout: 180000 });
      const d = r.data || {};
      toast.success(`Pack ready — ${d.count || 0} imported · ${(d.skipped_recently_imported || []).length} skipped`);
      onRunComplete?.(d);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Run failed");
    } finally { setRunningNow(false); }
  };

  if (loading) {
    return (
      <div
        data-testid="morning-pack-panel-loading"
        className="rounded-xl border border-line bg-bg-surface px-4 py-3 text-xs text-ink-muted inline-flex items-center gap-2"
      >
        <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading Morning Bed Pack…
      </div>
    );
  }

  const s = status || {};
  const hour = s.schedule?.hour_utc ?? 6;
  const weekdays = s.schedule?.weekdays_only;

  return (
    <div
      className="rounded-xl border border-line bg-bg-surface overflow-hidden"
      data-testid="morning-pack-panel"
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="morning-pack-toggle"
        className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-bg-elevated transition"
      >
        <div className="flex items-center gap-2">
          <Calendar className="h-4 w-4 text-brand-amber" />
          <div>
            <div className="text-xs font-mono uppercase tracking-wider text-brand-amber">// MORNING BED PACK</div>
            <div className="text-sm text-ink-primary">
              Auto-imports fresh tracks daily at {String(hour).padStart(2, "0")}:00 UTC{weekdays ? " · weekdays" : ""}
              {s.enabled ? <span className="ml-2 text-emerald-400">· ON</span> : <span className="ml-2 text-ink-muted">· OFF</span>}
            </div>
          </div>
        </div>
        {open ? <ChevronUp className="h-4 w-4 text-ink-muted" /> : <ChevronDown className="h-4 w-4 text-ink-muted" />}
      </button>

      {open && (
        <div className="border-t border-line px-4 py-4 space-y-4">
          {/* Schedule + Run Now */}
          <div className="flex flex-wrap items-center gap-3">
            <label className="inline-flex items-center gap-2 text-xs text-ink-primary">
              <Switch
                checked={!!s.enabled}
                onCheckedChange={(v) => save({ enabled: v })}
                disabled={saving}
                data-testid="morning-pack-enabled-switch"
              />
              Schedule enabled
            </label>
            <button
              type="button"
              onClick={runNow}
              disabled={runningNow || !s.enabled}
              data-testid="morning-pack-run-now-btn"
              className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition disabled:opacity-60"
              style={{ background: "linear-gradient(135deg, #4a9eff 0%, #6b5cf6 100%)", color: "#0a0d14" }}
              title="Fire today's pack immediately (same 18h per-genre cooldown applies)"
            >
              {runningNow
                ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Running…</>
                : <><Zap className="h-3.5 w-3.5" /> Run now</>}
            </button>
          </div>

          {/* Email digest */}
          <div className="rounded-lg border border-line bg-bg-elevated px-3 py-3">
            <div className="flex items-center gap-3">
              <Mail className="h-4 w-4 text-ink-secondary" />
              <div className="flex-1">
                <div className="text-xs font-semibold text-ink-primary">Email digest</div>
                <div className="text-[11px] text-ink-muted">A summary of fresh imports lands in your inbox after each run.</div>
              </div>
              <Switch
                checked={!!s.email_digest}
                onCheckedChange={(v) => save({ email_digest: v })}
                disabled={saving}
                data-testid="morning-pack-email-switch"
              />
            </div>
          </div>

          {/* Slack digest */}
          <div className="rounded-lg border border-line bg-bg-elevated px-3 py-3 space-y-3">
            <div className="flex items-center gap-3">
              <SlackIcon className="h-4 w-4 text-ink-secondary" />
              <div className="flex-1">
                <div className="text-xs font-semibold text-ink-primary">Slack digest</div>
                <div className="text-[11px] text-ink-muted">Pings your channel with the morning&apos;s fresh tracks. Paste an Incoming Webhook URL below.</div>
              </div>
              <Switch
                checked={!!s.slack_digest}
                onCheckedChange={(v) => save({ slack_digest: v })}
                disabled={saving}
                data-testid="morning-pack-slack-switch"
              />
            </div>

            <div className="flex gap-2">
              <Input
                placeholder={s.slack_webhook_set ? "•••••••• (saved)" : "https://hooks.slack.com/services/..."}
                value={webhookDraft}
                onChange={(e) => setWebhookDraft(e.target.value)}
                data-testid="morning-pack-slack-webhook-input"
                className="text-xs"
              />
              <button
                type="button"
                onClick={saveWebhook}
                disabled={saving || !webhookDraft.trim()}
                data-testid="morning-pack-slack-save-btn"
                className="px-3 py-1.5 rounded-md text-xs font-semibold bg-brand-amber text-black disabled:opacity-60"
              >
                Save
              </button>
              {s.slack_webhook_set && (
                <button
                  type="button"
                  onClick={() => save({ slack_webhook: "", slack_digest: false })}
                  disabled={saving}
                  data-testid="morning-pack-slack-clear-btn"
                  className="px-3 py-1.5 rounded-md text-xs font-semibold border border-line text-ink-secondary hover:text-ink-primary"
                >
                  Clear
                </button>
              )}
            </div>
          </div>

          {/* Slack /bedpack slash command */}
          <SlackSlashSection />

          {/* Recent runs */}
          <div>
            <div className="text-[11px] font-mono uppercase tracking-wider text-ink-muted mb-2">// LAST 7 RUNS</div>
            {!s.recent_runs?.length ? (
              <div className="text-xs text-ink-muted italic" data-testid="morning-pack-history-empty">
                No runs yet. The next scheduled fire is at {String(hour).padStart(2, "0")}:00 UTC.
              </div>
            ) : (
              <div className="space-y-1" data-testid="morning-pack-history">
                {s.recent_runs.map((r, i) => (
                  <div
                    key={i}
                    className="flex items-center gap-3 text-[11px] text-ink-secondary border-l-2 pl-2 py-0.5"
                    style={{ borderColor: r.ok ? "#10b981" : "#f87171" }}
                  >
                    <span className="font-mono text-ink-muted">{(r.created_at || "").slice(0, 16).replace("T", " ")}</span>
                    <span className="px-1.5 rounded bg-bg-elevated text-[10px]">{r.trigger || "scheduled"}</span>
                    {r.ok
                      ? <span>+{r.imported_count || 0} from {r.primary_source || "library"}</span>
                      : <span className="text-rose-400 truncate">{r.error || "failed"}</span>}
                    {r.digest && (
                      <span className="ml-auto text-ink-muted text-[10px]">
                        {r.digest.skipped
                          ? `digest: ${r.digest.skipped}`
                          : `${r.digest.slack?.ok ? "Slack✓" : ""}${r.digest.slack && r.digest.email ? " · " : ""}${r.digest.email?.ok ? "Email✓" : r.digest.email && !r.digest.email.ok ? "Email✗" : ""}`}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}


/**
 * Slack `/bedpack` slash-command configuration.
 *
 * Lives inside MorningPackPanel — kept as an internal component because
 * it shares the same Imaging Studio context but has its own loading /
 * saving lifecycle.
 */
function SlackSlashSection() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [cfg, setCfg] = useState(null);
  const [secretDraft, setSecretDraft] = useState("");
  const [copied, setCopied] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/imaging/library/morning-pack/slash-config");
      setCfg(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load /bedpack config");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const saveSecret = async () => {
    if (secretDraft && (secretDraft.length < 16 || secretDraft.length > 128)) {
      toast.error("Slack signing secret must be 16–128 characters");
      return;
    }
    setSaving(true);
    try {
      const r = await api.post("/imaging/library/morning-pack/slash-config", {
        slack_signing_secret: secretDraft,
      });
      setCfg(r.data);
      setSecretDraft("");
      toast.success("Saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const regenerate = async () => {
    setSaving(true);
    try {
      const r = await api.post("/imaging/library/morning-pack/slash-config", {
        regenerate_token: true,
      });
      setCfg(r.data);
      toast.success("New /bedpack URL generated — update your Slack app");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not regenerate");
    } finally { setSaving(false); }
  };

  const disable = async () => {
    setSaving(true);
    try {
      const r = await api.delete("/imaging/library/morning-pack/slash-config");
      setCfg(r.data);
      toast.success("/bedpack disabled");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not disable");
    } finally { setSaving(false); }
  };

  const copyUrl = async () => {
    if (!cfg?.command_url) return;
    try {
      await navigator.clipboard.writeText(cfg.command_url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Could not copy — select the URL manually");
    }
  };

  if (loading) {
    return (
      <div className="rounded-lg border border-line bg-bg-elevated px-3 py-3 text-xs text-ink-muted inline-flex items-center gap-2">
        <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading /bedpack…
      </div>
    );
  }

  const c = cfg || {};
  const ready = c.slash_token_set && c.signing_secret_set;

  return (
    <div className="rounded-lg border border-line bg-bg-elevated px-3 py-3 space-y-3" data-testid="slack-slash-section">
      <div className="flex items-start gap-3">
        <Terminal className="h-4 w-4 mt-0.5 text-ink-secondary" />
        <div className="flex-1">
          <div className="text-xs font-semibold text-ink-primary flex items-center gap-2">
            <span>Slash command <code className="font-mono bg-bg-surface px-1.5 py-0.5 rounded text-brand-amber">/bedpack</code></span>
            {ready
              ? <span className="text-[10px] text-emerald-400 font-mono uppercase tracking-wider">· LIVE</span>
              : <span className="text-[10px] text-ink-muted font-mono uppercase tracking-wider">· not configured</span>}
          </div>
          <div className="text-[11px] text-ink-muted">
            Trigger your Morning Bed Pack from inside Slack. Digest comes back in the same channel.
          </div>
        </div>
        <button
          type="button"
          onClick={() => setShowInstructions((v) => !v)}
          data-testid="slash-instructions-toggle"
          className="text-[10px] font-mono uppercase tracking-wider text-brand-amber hover:underline"
        >
          {showInstructions ? "Hide setup" : "Setup steps"}
        </button>
      </div>

      {showInstructions && (
        <div className="text-[11px] text-ink-secondary border-l-2 border-brand-amber pl-3 space-y-1.5" data-testid="slash-instructions">
          <div><strong>1.</strong> Open <a href="https://api.slack.com/apps" target="_blank" rel="noreferrer" className="text-brand-amber hover:underline">api.slack.com/apps</a> and create a new app (or pick an existing one).</div>
          <div><strong>2.</strong> Under <em>Features → Slash Commands</em>, click <em>Create New Command</em>:</div>
          <div className="pl-4 font-mono text-[10px] text-ink-muted">Command: /bedpack · URL: paste yours below · Description: Run Morning Bed Pack</div>
          <div><strong>3.</strong> Under <em>Settings → Basic Information</em>, copy the <em>Signing Secret</em> and paste it below.</div>
          <div><strong>4.</strong> Install the app to your workspace. Type <code className="bg-bg-surface px-1 rounded text-brand-amber">/bedpack</code> in any channel.</div>
        </div>
      )}

      {/* Signing secret row */}
      <div className="flex gap-2">
        <Input
          placeholder={c.signing_secret_set ? "•••••••• (saved)" : "Paste Slack Signing Secret"}
          value={secretDraft}
          onChange={(e) => setSecretDraft(e.target.value)}
          data-testid="slash-secret-input"
          type="password"
          className="text-xs"
        />
        <button
          type="button"
          onClick={saveSecret}
          disabled={saving || !secretDraft.trim()}
          data-testid="slash-secret-save-btn"
          className="px-3 py-1.5 rounded-md text-xs font-semibold bg-brand-amber text-black disabled:opacity-60"
        >
          {c.slash_token_set ? "Update" : "Enable"}
        </button>
      </div>

      {/* Command URL row — only show once both the token AND secret are set */}
      {ready && (
        <div className="space-y-2" data-testid="slash-command-url-block">
          <div className="text-[10px] font-mono uppercase tracking-wider text-ink-muted">// COMMAND URL</div>
          <div className="flex gap-2 items-center">
            <code
              className="flex-1 text-[11px] font-mono text-ink-primary bg-bg-surface border border-line rounded px-2 py-1.5 truncate"
              data-testid="slash-command-url"
              title={c.command_url}
            >
              {c.command_url}
            </code>
            <button
              type="button"
              onClick={copyUrl}
              data-testid="slash-copy-btn"
              className="px-3 py-1.5 rounded-md text-xs font-semibold border border-line text-ink-secondary hover:text-ink-primary inline-flex items-center gap-1.5"
            >
              {copied ? <><Check className="h-3.5 w-3.5 text-emerald-400" /> Copied</> : <><Copy className="h-3.5 w-3.5" /> Copy</>}
            </button>
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={regenerate}
              disabled={saving}
              data-testid="slash-regenerate-btn"
              className="px-3 py-1.5 rounded-md text-xs font-semibold border border-line text-ink-secondary hover:text-ink-primary inline-flex items-center gap-1.5"
            >
              <RefreshCw className="h-3.5 w-3.5" /> Regenerate URL
            </button>
            <button
              type="button"
              onClick={disable}
              disabled={saving}
              data-testid="slash-disable-btn"
              className="px-3 py-1.5 rounded-md text-xs font-semibold border border-line text-rose-400 hover:bg-rose-500/10"
            >
              Disable
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
